import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

/** Same key as public/theme-init.js, which applies the theme before React starts. */
export const THEME_STORAGE_KEY = 'crm-theme';

const listeners = new Set<() => void>();

function currentTheme(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/** Switches the theme for this browser and remembers it (a per-viewer convenience only). */
export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage can be blocked (private mode); the theme still applies for this visit.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const theme = useSyncExternalStore(subscribe, currentTheme, () => 'light' as const);
  const toggle = useCallback(() => applyTheme(theme === 'dark' ? 'light' : 'dark'), [theme]);
  return { theme, toggle };
}
